import { AppTextField } from 'components/core/AppTextField/AppTextField';
import { AlluraFormikErrorMessage } from 'components/formik/core/AlluraErrorMessage/AlluraFormikErrorMessage';
import { useField, useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useUserCheckEmailTaken } from '@alluradirect/react';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { Box, Typography } from '@mui/material';
import { AppLink } from 'components/core/AppLink/AppLink';
import { AppRoutes } from 'config/appRoutes';

interface Props {
  name: string;

  autoFocus?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onChange?: () => void;
  size?: 'small' | 'medium' | undefined;

  endAdornment?: React.ReactNode;
}

export const FormikAppEmail = ({
  name,
  autoFocus = false,
  disabled = false,
  size = 'small',
  onBlur = undefined,
  onFocus = undefined,
  onChange = undefined,
  endAdornment = undefined,
}: Props) => {
  const [field, , helpers] = useField(name);

  const focus = useCallback(() => {
    onFocus && onFocus();
  }, [onFocus]);

  const blur = useCallback(
    (e: any) => {
      onBlur && onBlur();
      field.onBlur(e);
      helpers.setTouched(true);
    },
    [field, helpers, onBlur],
  );

  const handleOnChange = (e: any) => {
    onChange && onChange();
    helpers.setValue(e.target.value.trim());
  };

  return (
    <>
      <AppTextField
        label="Email"
        variant="outlined"
        size={size}
        onFocus={focus}
        onBlur={blur}
        fullWidth
        autoComplete={'email'}
        type="email"
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={handleOnChange}
        value={field.value}
        InputProps={{
          endAdornment,
        }}
      />
      <AlluraFormikErrorMessage fieldName={name} />
    </>
  );
};

interface FormikAppEmailCheckTakenProps {
  onCheckTakenSuccess?: (data: any) => void;
  loginRoute?: AppRoutes.loginGuest | AppRoutes.loginHost;
}

export const FormikAppEmailCheckTaken = ({
  onCheckTakenSuccess,
  loginRoute = AppRoutes.loginGuest,
  ...props
}: Props & FormikAppEmailCheckTakenProps) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(props.name);

  const isCheckTakenEnabled = !meta.error && field.value.length >= 3;

  const emailTaken = useUserCheckEmailTaken({
    email: field.value,
    enabled: Boolean(isCheckTakenEnabled),
    onSuccess: data => {
      const isTaken = data?.DATA?.IS_EMAIL_TAKEN;
      setFieldValue('is_email_taken', isTaken);
      onCheckTakenSuccess && onCheckTakenSuccess(data);
    },
  });

  const resetEmailTaken = () => {
    setFieldValue('is_email_taken', false);
  };

  const isEmailTaken = emailTaken.data?.DATA?.IS_EMAIL_TAKEN;

  return (
    <>
      <FormikAppEmail
        {...props}
        onChange={resetEmailTaken}
        endAdornment={
          emailTaken.isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={14} thickness={1} color="primary" />
            </InputAdornment>
          ) : undefined
        }
      />
      {isEmailTaken ? (
        <Box>
          <Typography variant="caption" color={'warning.main'}>
            Email Already Exists <AppLink href={loginRoute}>Log In?</AppLink>
          </Typography>
        </Box>
      ) : null}
    </>
  );
};
